import {InputText} from '@/components/commons/input-text/InputText'
import {Button} from '@/components/ui/button/Button'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {PlusIcon, Trash01Icon} from '@/components/ui/icon/Icon'
import {InputSelectAccessType} from '@/features/access-types/components/input-select-access-type/InputSelectAccessType'
import {disabledAccessTypeByProjectType} from '@/features/access-types/utils'
import {PlannedAccessesFieldsSchema} from '@/features/planned-accesses/formSchemas'
import {ProjectExtended} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {useMemo} from 'react'
import {useOutletContext} from 'react-router-dom'
import {z} from 'zod'

const PlannedAccessesSingleAccessFieldsSchema = PlannedAccessesFieldsSchema.pick({
    singleAccesses: true,
    project: true
})
type PlannedAccessesSingleAccessFieldsSchema = z.infer<typeof PlannedAccessesSingleAccessFieldsSchema>

export const PlannedAccessesSingleAccessFields = () => {
    const {t} = useTranslation()
    const {control, formState} = useFormContext<PlannedAccessesSingleAccessFieldsSchema>()
    const {project} = useOutletContext<{project: ProjectExtended}>()
    const {fields, append, remove} = useFieldArray({
        control,
        name: 'singleAccesses',
        keyName: 'fieldId'
    })

    const watchSingleAccesses = useWatch({control, name: 'singleAccesses'})

    const onAddSingleAccess = () => {
        append({
            accessType: undefined,
            numberOfAccesses: undefined
        })
    }

    const disabledAccessTypes = useMemo(() => {
        const alreadySelectedAccessTypes = watchSingleAccesses
            .map(field => field.accessType?.accessType)
            .filter((accessType): accessType is NonNullable<typeof accessType> => !!accessType)

        if (!project?.type)
            return [
                ...disabledAccessTypeByProjectType.cyclic,
                ...disabledAccessTypeByProjectType.continuous,
                ...alreadySelectedAccessTypes
            ]

        return [...disabledAccessTypeByProjectType[project.type], ...alreadySelectedAccessTypes]
    }, [project?.type, watchSingleAccesses])

    return (
        <>
            {fields.map((field, index) => (
                <Flexbox key={field.fieldId} fullWidth gap={2} align="flex-end">
                    <Grid columns={3} fullWidth gap={2.5}>
                        <GridItem>
                            <Controller
                                control={control}
                                name={`singleAccesses.${index}.accessType`}
                                render={({field}) => (
                                    <InputSelectAccessType
                                        label={t('commons:access_type')}
                                        placeholder={t('commons:select')}
                                        onChange={field.onChange}
                                        value={field.value}
                                        errorMessage={formState.errors?.singleAccesses?.[index]?.accessType?.message}
                                        disabledAccessTypes={disabledAccessTypes}
                                        groupName="in_person_accesses"
                                    />
                                )}
                            />
                        </GridItem>
                        <GridItem justify="space-between" gap={2}>
                            <Controller
                                control={control}
                                name={`singleAccesses.${index}.numberOfAccesses`}
                                render={({field}) => (
                                    <InputText
                                        label={t('commons:number')}
                                        type="number"
                                        value={field.value ?? ''}
                                        onChange={event =>
                                            field.onChange(event.target.value ? parseInt(event.target.value) : '')
                                        }
                                        placeholder="-"
                                        errorMessage={
                                            formState.errors?.singleAccesses?.[index]?.numberOfAccesses?.message
                                        }
                                    />
                                )}
                            />
                        </GridItem>
                        <GridItem align="flex-end">
                            <Button
                                variant="ghost"
                                onClick={() => remove(index)}
                                shape="square"
                                disabled={fields.length == 1}
                            >
                                <Trash01Icon />
                            </Button>
                        </GridItem>
                    </Grid>
                </Flexbox>
            ))}
            <Divider direction="horizontal" />
            <Button variant="secondary" size="sm" onClick={onAddSingleAccess}>
                <PlusIcon />
                {t('planned_accesses:single_access')}
            </Button>
        </>
    )
}
